import { createTheme, type Theme } from '@admin/shared/themes/default';
import { alpha } from '@material-ui/core/styles/colorManipulator';
import { deepmerge } from '@material-ui/utils';

import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/500.css';
import '@fontsource/open-sans/700.css';

import { Themes } from '@admin/shared/themes/themes';

// userlike color mapping (1 - 100)
const grey = {
  '1': '#fbfbfb',   // grey 100
  '12': '#e0e2e4',  // grey 300
  '40': '#999fa5',  // grey 200
  '60': '#667078',  // grey 400, all icons
  '80': '#33404b',  // grey 600
  '100': '#00101F', // grey 800, for all texts
};

/**
 * Create theme for userlike integration.
 *
 * Override existing styles to match the userlike admin UI.
 *
 * @returns {Theme}
 */
export const createMappTheme = (): Theme => {
  const theme = createTheme(
    {
      primary: '#ea3368',
      font: '"Open Sans", "Helvetica", "Arial", sans-serif',
    },
    // $FlowExpectedError
    {
      palette: {
        // material UI - color mapping (100 - 1000)
        grey: {
          '100': grey['1'],
          '200': grey['40'],
          '300': grey['12'],
          '350': grey['40'],
          '400': grey['60'],
          '600': grey['80'],
          '800': grey['100'],
        },

        text: {
          primary: '#000',
        },

        icon: {
          primary: grey['80'],
        },
      },
      props: {
        meta: {
          name: Themes.MAPP,
        },
      },
    },
  );

  theme.overrides = deepmerge(theme.overrides, {
    MuiCssBaseline: {
      '@global': {
        body: {
          color: theme.palette.grey['800'],
        },
      },
    },

    MuiIconButton: {
      root: {
        color: theme.palette.grey['400'],
      },
    },

    OMQAdminModuleNavigation: {
      root: {
        flex: '0 0 64px',
        backgroundColor: theme.palette.common.black,
        boxShadow: 'rgb(209, 214, 222) -1px 0px 0px inset',
        padding: 12,
        background: theme.palette.common.black,
      },

      icon: {
        '&:hover': {
          color: theme.palette.primary.main,
          background: theme.palette.common.black,
        },

        '&$selected': {
          color: theme.palette.primary.main,
          background: alpha(theme.palette.primary.main, 0.16),
        },
      },

      dashboardIcon: {
        marginBottom: 36,

        '& svg': {
          color: theme.palette.primary.main,
          width: 40,
          height: 40,
        },
      },
    },

    OMQAdminModuleMenu: {
      root: {
        boxShadow: 'rgb(209, 214, 222) -1px 0px 0px inset',
        paddingRight: 1,
        paddingTop: 17,
        background: '#fbfbfb',
      },

      headline: {
        fontSize: 20,
        fontWeight: 'bold',
        lineHeight: '30px',
      },

      listWrapper: {
        '&::after': {
          background: 'linear-gradient(0deg, #fbfbfb, #fbfbfb 10%, transparent)',
        },
      },
    },

    OMQMenuHeadline: {
      text: {
        '&$isActive': {
          color: theme.palette.text.primary,
        },
      },
    },

    OMQEditor: {
      root: {
        borderWidth: 2,
        borderColor: theme.palette.grey['300'],
      },
    },

    DEContentEditable: {
      root: {
        '&:empty::after': {
          color: theme.palette.grey['200'],
        },
      },
    },

    DEDownloadElement: {
      downloadItem: {
        color: theme.palette.text.primary,
      },
    },

    MuiInputBase: {
      input: {
        lineHeight: '18px',
        padding: '11px 12px',
      },
    },

    MuiOutlinedInput: {
      root: {
        '&$focused $notchedOutline': {
          borderWidth: 2,
        },
      },
      notchedOutline: {
        borderWidth: 2,
      },
    },

    MuiSwitch: {
      switchBase: {
        '&:hover': {
          color: theme.palette.common.white,
        },
      },
    },

    OMQSelect: {
      paper: {
        borderWidth: 2,
      },
      paperTop: {
        transform: 'translateY(0px) !important',
      },
      paperBottom: {
        transform: 'translateY(-1px) !important',
      },
    },

    OMQDesk: {
      navigation: {
        background: theme.palette.common.black,
      },
    },

    OMQDeskContent: {
      content: {
        background: '#fbfbfb',
        padding: '17px 24px',
      },
    },

    OMQDeskHeader: {
      name: {
        fontSize: 20,
        fontWeight: 'bold',
        lineHeight: '30px',
      },
    },

    OMQDeskModuleItem: {
      root: {
        transition: 'box-shadow 125ms ease-out 0s',

        '&:hover': {
          boxShadow: 'rgb(0 0 0 / 24%) 0px 8px 20px',
        },
      },
    },
  });

  return theme;
};
